import React, {
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import noop from '@wix/da-shared-react/pkg/utils/noop';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import cn from 'classnames';
import UserField from '../../components/UserInput';
import { getDraftParticipants } from '../../redux/selectors/drafts';
import { useDispatch, useSelector } from 'react-redux';
import {
  addDraftUser,
  clearDraft,
  removeDraftUser,
  removeDraftUserAll,
} from '../../redux/actions/drafts';
import { NON_CHAT_USERS, TEMP_DRAFT_ROOM_ID } from '../../constants';
import InputErrorMessage from '@wix/da-ds/pkg/formControls/lego/InputErrorMessage';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import ListItemRadioButton from '@wix/da-ds/pkg/List/ListItem/dsPresets/ListItemRadioButton';
import GroupIcon from '@wix/da-ds/pkg/Icons/16x16/ManageMembers';
import CommunityIcon from '@wix/da-ds/pkg/Icons/24x24/CreateChannel';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import CoreSymbol from '@wix/da-ds/pkg/Indicators/CoreSymbol';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { BiData, BuyCoreButtonBiEvent } from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import BuyCoreBrandedButton from '@wix/da-shared-react/pkg/Button/core/dsPresets/BuyCoreBrandedButton';
import { createCommunityChannel, createRoom } from '../../redux/actions/room';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks/useFlag';
import { setAltchaToken } from '../../redux/actions/altcha';
import { getAltchaToken } from '../../redux/selectors/altcha';

// Have to lazy load as this will get included in SSR build and web components are not supported in SSR.
const AltchaFloating = lazy(
  () => import('@wix/da-ds/pkg/Altcha/AltchaFloating')
);

import s from './CreateGroupOrChannelModal.scss';

export interface Props {
  onRequestClose?: () => void;
}

const MIN_TITLE_LENGTH = 1;
const MAX_TITLE_LENGTH = 50;

const altchaConfig: AltchaWidgetOptions = {
  debug: true,
};

const CreateGroupOrChannelModal: React.FC<Props> = ({
  onRequestClose = noop,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('damz');
  const users = useSelector(getDraftParticipants);
  const [roomName, setRoomName] = useState('');
  const [nameError, setNameError] = useState('');
  const [selectedType, setSelectedType] = useState<'group' | 'channel'>(
    'group'
  );
  const { hasCoreMembership } = useContext(SessionContext);
  const upsell = !hasCoreMembership && selectedType !== 'group';
  const floatingAnchorRef = useRef<HTMLDivElement>(null);
  const [isMounted, setIsMounted] = useState(false);

  const isAltcha = useFlag('altcha_damz');
  const altchaToken = useSelector(getAltchaToken);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const handleAddUser = useCallback(
    user => {
      dispatch(addDraftUser(user, TEMP_DRAFT_ROOM_ID));
    },
    [dispatch]
  );

  const handleRemoveAllUsers = useCallback(() => {
    dispatch(removeDraftUserAll(TEMP_DRAFT_ROOM_ID));
  }, [dispatch]);

  const handleRemoveUser = useCallback(
    user => {
      dispatch(removeDraftUser(user, TEMP_DRAFT_ROOM_ID));
    },
    [dispatch]
  );

  const maximumUsersReached =
    users.length >= (selectedType === 'group' ? 20 : 50);

  const validateName = useCallback(
    (name: string) => {
      if (selectedType === 'channel' && name.trim().length === 0) {
        setNameError(t('damz.modal.createRoom.error.nameRequired'));
        return false;
      }
      if (name.trim().length > 0 && name.trim().length < MIN_TITLE_LENGTH) {
        setNameError(
          t('damz.modal.createRoom.error.nameMinLength', {
            minLength: MIN_TITLE_LENGTH,
          })
        );
        return false;
      }
      setNameError('');
      return true;
    },
    [selectedType, t]
  );

  let isFormValid = false;

  const titleValid =
    roomName.trim().length >= MIN_TITLE_LENGTH &&
    roomName.trim().length <= MAX_TITLE_LENGTH;

  if (
    selectedType === 'group' &&
    users.length > 0 &&
    roomName.trim().length <= MAX_TITLE_LENGTH
  ) {
    isFormValid = true;
  }

  if (selectedType === 'channel' && titleValid) {
    isFormValid = true;
  }

  if (isAltcha && !altchaToken) {
    isFormValid = false;
  }

  const handleCreate = () => {
    if (isAltcha && !altchaToken) {
      return;
    }

    if (validateName(roomName)) {
      if (isFormValid) {
        if (selectedType === 'group') {
          dispatch(
            createRoom(
              'MULTI',
              users.map(user => user.useridUuid),
              roomName
            )
          );
        } else {
          dispatch(
            createCommunityChannel(
              roomName,
              users.map(user => user.useridUuid)
            )
          );
        }
        dispatch(clearDraft(TEMP_DRAFT_ROOM_ID));
        onRequestClose();
      }
    }
  };

  const handleVerificationDone = useCallback(
    (token: { payload: string; state: AltchaState }) => {
      dispatch(setAltchaToken(token.payload));
    },
    [dispatch]
  );

  return (
    <Modal
      className={s['root']}
      isOpen
      onRequestClose={onRequestClose}
      heading={t('damz.modal.createRoom.title')}
      contentClassName={s['content']}
      isFullscreen={false}
      footerRight={[
        <TextButton
          key="cancel"
          size="large"
          variant="main"
          onClick={onRequestClose}
        >
          {t('damz.modal.createRoom.cancel')}
        </TextButton>,
        !upsell && !isAltcha && (
          <BrandedButton
            key="create"
            size="large"
            variant="brand-green"
            onClick={handleCreate}
            disabled={!isFormValid}
          >
            {t('damz.modal.createRoom.create')}
          </BrandedButton>
        ),
        !upsell && isAltcha && (
          <div key="create" ref={floatingAnchorRef}>
            <BrandedButton
              size="large"
              variant="brand-green"
              onClick={handleCreate}
              disabled={!isFormValid || !altchaToken}
            >
              {t('damz.modal.createRoom.create')}
            </BrandedButton>
            {isMounted && (
              <Suspense fallback={null}>
                <AltchaFloating
                  isVisible={!altchaToken}
                  buttonRef={floatingAnchorRef}
                  onVerificationDone={handleVerificationDone}
                  config={altchaConfig}
                />
              </Suspense>
            )}
          </div>
        ),
        upsell && (
          <BuyCoreBrandedButton
            key="create"
            size="large"
            biData={BiData<BuyCoreButtonBiEvent>({
              evid: 375,
              referral_info: ReferralType.MESSAGES_CREATE_CHANNEL,
            })}
          >
            {t('damz.modal.createRoom.buyCore')}
          </BuyCoreBrandedButton>
        ),
      ]}
    >
      <div className={s['radio-button-container']}>
        <ListItemRadioButton
          className={cn(
            s['radio-button'],
            selectedType === 'group' && s['selected']
          )}
          value={'group'}
          name="content_filter"
          label={
            <span className={s['radio-label']}>
              <GroupIcon size={IconSize.MEDIUM} />
              <h3>{t(`damz.modal.createRoom.radio.group.label`)}</h3>
              <p>{t(`damz.modal.createRoom.radio.group.sublabel`)}</p>
            </span>
          }
          checked={selectedType === 'group'}
          onChange={() => setSelectedType('group')}
        />
        <ListItemRadioButton
          className={cn(
            s['radio-button'],
            selectedType === 'channel' && s['selected']
          )}
          value={'channel'}
          name="content_filter"
          label={
            <span className={s['radio-label']}>
              <CommunityIcon size={IconSize.MEDIUM} />
              <h3>
                {t(`damz.modal.createRoom.radio.channel.label`)}{' '}
                <CoreSymbol size={IconSize.SMALL} />
              </h3>
              <p>{t(`damz.modal.createRoom.radio.channel.sublabel`)}</p>
            </span>
          }
          checked={selectedType === 'channel'}
          onChange={() => setSelectedType('channel')}
        />
      </div>
      <div className={s['selected-info']}>
        {selectedType === 'group'
          ? t('damz.modal.createRoom.selected.info.group')
          : t('damz.modal.createRoom.selected.info.channel')}
      </div>
      {upsell && (
        <div className={s['upsell-info']}>
          {t('damz.modal.createRoom.upsell.info', {
            postProcess: 'reactPostprocessor',
            learnMoreLink: (
              // eslint-disable-next-line react/jsx-no-target-blank
              <a
                href={t('damz.modal.createRoom.upsell.info.url')}
                target="_blank"
                className="text-link-level-2-secondary"
              >
                {t('damz.modal.createRoom.upsell.info.link')}
              </a>
            ),
          })}
        </div>
      )}
      {(hasCoreMembership || selectedType === 'group') && (
        <>
          <div className={cn(s['user-field-container'])}>
            <div>{t('damz.modal.createRoom.to')}</div>
            <div className={s['user-field-wrapper']}>
              <UserField
                className={s['user-field']}
                placeholder={t('damz.user.field.placeholder')}
                withClearButton
                users={users}
                onAddUser={handleAddUser}
                onRemoveAll={handleRemoveAllUsers}
                onRemoveUser={handleRemoveUser}
                disabled={maximumUsersReached}
                additionalExcludedUsers={NON_CHAT_USERS}
              />
              {maximumUsersReached && (
                <InputErrorMessage>
                  {t('damz.modal.createRoom.error.maxUsers')}
                </InputErrorMessage>
              )}
            </div>
          </div>
          <div className={s['room-name-container']}>
            <TextInput
              maxLength={MAX_TITLE_LENGTH}
              isRequired={selectedType === 'channel'}
              id="name"
              label={t('damz.modal.createRoom.roomName')}
              value={roomName}
              onChange={event => {
                const newValue = event.target.value;
                setRoomName(newValue);
                if (nameError && newValue.trim().length >= MIN_TITLE_LENGTH) {
                  setNameError('');
                }
              }}
              onBlur={() => validateName(roomName)}
              placeholder={t('damz.modal.createRoom.roomNamePlaceholder')}
              validationResult={nameError ? 'error' : undefined}
              details={nameError}
            />
          </div>
        </>
      )}
    </Modal>
  );
};
CreateGroupOrChannelModal.displayName = 'CreateGroupOrChannelModal';

export default CreateGroupOrChannelModal;
